
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Clock, Phone, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { lazy, Suspense } from 'react';
import { Studio } from '@/hooks/use-studios';

// Dynamically import the map component to avoid SSR issues
// Using React.lazy instead of Next.js dynamic
const StudioMap = lazy(() => import('@/components/booking/StudioMap'));

interface StudioLocationProps {
  studio: Studio;
}

export const StudioLocation: React.FC<StudioLocationProps> = ({ studio }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Standort & Öffnungszeiten</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Map */}
          <div className="h-[300px] md:h-[400px] rounded-xl overflow-hidden border border-zinc-800">
            {studio.geo_location ? (
              <Suspense fallback={
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <p className="text-zinc-500">Karte wird geladen...</p>
                </div>
              }>
                <StudioMap
                  studios={[studio]}
                  selectedStudio={studio}
                  onStudioSelect={() => {}}
                />
              </Suspense>
            ) : (
              <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                <p className="text-zinc-500">Keine Standortdaten verfügbar</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Contact info */}
          <Card className="bg-card/50 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Kontakt & Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#2C6E49] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{studio.name}</p>
                  <p className="text-sm text-zinc-400">{studio.address || 'Adresse nicht verfügbar'}</p>
                </div>
              </div>
              
              {studio.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#2C6E49] flex-shrink-0" />
                  <p className="text-sm">{studio.phone}</p>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-[#2C6E49] flex-shrink-0" />
                <p className="text-sm">chrona.app/studio/{studio.slug || studio.id}</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Opening hours */}
          <Card className="bg-card/50 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Öffnungszeiten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr'].map(day => (
                  <div key={day} className="flex justify-between">
                    <span className="font-medium">{day}</span>
                    <span className="text-zinc-300">9:00 - 18:00</span>
                  </div>
                ))}
                <div className="flex justify-between">
                  <span className="font-medium">Sa</span>
                  <span className="text-zinc-300">10:00 - 16:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">So</span>
                  <span className="text-zinc-300">Geschlossen</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
